import React, { useEffect, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Box,
  Button,
  HStack,
  Link,
  Skeleton,
  SkeletonText,
  VStack,
} from "@chakra-ui/react";
import { Table, Tbody, Td, Th, Thead, Tr } from "@chakra-ui/table";
import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { PageContent } from "../../components/PageContent/PageContent";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { ViewControl } from "../../components/ViewControl/ViewControl";
import { EditIcon } from "../../styles/icons";
import { useAdminApiClient } from "../../hooks/useApiClient";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import { useToast } from "../../hooks/useToast";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { GeoLocationGroupDetailForm } from "../../forms/GeoLocationGroupDetailForm";

export const GeoLocationGroupDetail: React.FC = () => {
  const { businessId, geoLocationGroupId } = useParams();
  const apiClient = useAdminApiClient();
  const { showSuccessToast, showErrorToast } = useToast();

  const { data: geoLocationGroupDoc, isLoading } = useAdminApiRequest(
    (apiClient) =>
      businessId && geoLocationGroupId
        ? apiClient.getGeoLocationGroup(businessId, geoLocationGroupId)
        : null,
    [businessId, geoLocationGroupId]
  );

  const [geoLocationGroup, setGeoLocationGroup] =
    useState<CherryHubAdminApi.GeoLocationGroup | null>(null);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    if (geoLocationGroupDoc) setGeoLocationGroup(geoLocationGroupDoc);
  }, [geoLocationGroupDoc]);

  const onSuccess = (updated: CherryHubAdminApi.GeoLocationGroup) => {
    setGeoLocationGroup(updated);
    setEditMode(false);
    showSuccessToast("Geo Location Group updated.");
  };

  const onFailure = (message?: string) => {
    showErrorToast(message ?? "Failed to update Geo Location Group.");
  };

  return (
    <>
      <PageHeading>
        <PageHeading.Title>Geo Location Group</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" width="100%" spacing={6}>
          <BusinessBreadcrumbs variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink
                as={RouterLink}
                to={`/admin/businesses/${businessId}/geo-fence-configuration`}
              >
                Geo-Fence Configuration
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink
                as={RouterLink}
                to={`/admin/businesses/${businessId}/geo-fence-configuration/geo-location-groups/${geoLocationGroupId}`}
              >
                {geoLocationGroup?.DisplayName ??
                  geoLocationGroup?.GeoLocationGroupId ??
                  "-"}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>

          {isLoading && (
            <Box w="100%">
              <Skeleton w="320px" height="32px" mb={4} />
              <SkeletonText noOfLines={8} spacing="4" />
            </Box>
          )}

          {!isLoading && geoLocationGroup && editMode && businessId && (
            <GeoLocationGroupDetailForm
              apiClient={apiClient}
              businessId={businessId}
              geoLocationGroup={geoLocationGroup}
              onCancel={() => setEditMode(false)}
              onSuccess={onSuccess}
              onFailure={onFailure}
            />
          )}

          {!isLoading && geoLocationGroup && !editMode && (
            <>
              <HStack w="100%" justifyContent="flex-end">
                <Button
                  leftIcon={<EditIcon />}
                  colorScheme="cherryButton"
                  onClick={() => setEditMode(true)}
                >
                  Edit
                </Button>
              </HStack>

              <VStack w="100%" alignItems="flex-start" spacing={2}>
                <ViewControl
                  label="Display Name"
                  value={geoLocationGroup.DisplayName ?? "-"}
                />
                <ViewControl
                  label="Geo Location Group Id"
                  value={geoLocationGroup.GeoLocationGroupId}
                />
                <ViewControl label="Geo Locations">
                  <Box flex={3}>
                    <Table size="sm">
                      <Thead>
                        <Tr>
                          <Th>Geo Location Id</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {(geoLocationGroup.GeoLocationIds?.length ?? 0) > 0 ? (
                          geoLocationGroup.GeoLocationIds!.map((id) => (
                            <Tr key={id}>
                              <Td whiteSpace="nowrap">
                                <Link
                                  as={RouterLink}
                                  to={`/admin/businesses/${businessId}/geo-fence-configuration/geo-locations/${id}`}
                                  textDecoration="none"
                                  _hover={{ textDecoration: "underline" }}
                                >
                                  {id}
                                </Link>
                              </Td>
                            </Tr>
                          ))
                        ) : (
                          <Tr>
                            <Td textAlign="center">
                              No geo locations in this group.
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </Box>
                </ViewControl>
              </VStack>
            </>
          )}
        </VStack>
      </PageContent>
    </>
  );
};
